/**
 * /api/me — current user hydration.
 *
 * Legacy reference: main.js §4 `/api/me` flow (lines 103–701).
 * The shape below is intentionally a subset that matches what phase 1
 * needs to verify the token + render the avatar. Additional fields land
 * with the profile migration (phase 3.9).
 */

import { apiFetch } from "./client.js";
import { ENDPOINTS } from "./endpoints.js";
import { ApiError } from "./errors.js";

export type MeUser = {
  id: string;
  name?: string;
  displayName?: string;
  username?: string;
  handle?: string;
  /**
   * H5 main.js:561 + mock people fixtures (1804-1888) carry an explicit
   * single-character `initials` field that drives `.head-avatar` / chat
   * bubble glyphs. Backend echoes it through; React derives a fallback from
   * `name`/`username` when missing (see MenuPage.tsx).
   */
  initials?: string;
  avatar?: string;
  color?: string;
  bio?: string;
  placementCredit?: {
    used?: number | null;
    max?: number | null;
  } | null;
  usedHighLevels?: Record<string, number | null>;
  highLevelQuota?: Record<string, number | null>;
};

export async function fetchMe(signal?: AbortSignal): Promise<MeUser | null> {
  try {
    const me = await apiFetch<MeUser | { user?: MeUser } | null>(ENDPOINTS.me, {
      method: "GET",
      auth: true,
      signal,
    });
    if (!me) return null;
    // Some backends nest the user object under { user: ... }
    if (typeof me === "object" && "user" in me && me.user) return me.user;
    return me as MeUser;
  } catch (err) {
    // 401 already clears the token inside apiFetch; treat as "not logged in".
    if (err instanceof ApiError && err.isUnauthorized) {
      return null;
    }
    throw err;
  }
}
